import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface FAQSectionProps {
    project: any;
}

const FAQSection: React.FC<FAQSectionProps> = ({ project }) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(0);
    const [showAll, setShowAll] = useState(false);

    if (!project.faqs || project.faqs.length === 0) return null;

    const displayedFaqs = showAll ? project.faqs : project.faqs.slice(0, 4);

    return (
        <section id="faq-section" className="mb-4 scroll-mt-32">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                
                {/* Clean Professional Header */}
                <div className="p-4 md:p-5 border-b border-slate-100">
                    <div className="flex items-center">
                        <h4 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] leading-none ml-1">
                            Frequently Asked Questions
                        </h4>
                        <SectionInfoBox text={project.sectionInfo?.faq} />
                    </div>
                    <p className="text-black text-[12px] md:text-[13px] font-medium mt-2 ml-1">
                        Everything you need to know about {project.name}
                    </p>
                </div>

                {/* FAQ Content - Unified List */}
                <div className="divide-y divide-slate-200">
                    {displayedFaqs.map((faq: any, idx: number) => {
                        const isActive = activeIndex === idx;
                        return (
                            <div key={idx} className="bg-white transition-colors duration-300">
                                <button
                                    onClick={() => setActiveIndex(isActive ? null : idx)}
                                    className="w-full flex items-center justify-between p-3.5 md:p-4.5 text-left group"
                                >
                                    <span className={`text-[14px] md:text-base pr-6 transition-colors duration-300 font-bold text-black`}>
                                        {faq.question}
                                    </span>
                                    
                                    <div className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-all duration-300 ${
                                        isActive ? 'rotate-180 text-[#05144b]' : 'text-[#05144b]/30 group-hover:text-[#05144b]'
                                    }`}>
                                        <i className="fa-solid fa-chevron-down text-[12px]"></i>
                                    </div>
                                </button>

                                <AnimatePresence>
                                    {isActive && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                        >
                                            <div className="px-3.5 md:px-4.5 pb-4 pt-0">
                                                <div className="p-3 md:p-4 bg-slate-50 rounded-xl border border-slate-100">
                                                    <p className="text-black text-[14px] md:text-[16px] leading-relaxed font-medium">
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                {/* View More Button */}
                {project.faqs.length > 4 && (
                    <div className="p-4 border-t border-slate-100 bg-slate-50/20 flex justify-center">
                        <button 
                            onClick={() => setShowAll(!showAll)}
                            className="text-[12px] md:text-[13px] font-black bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white px-8 py-2.5 rounded-full transition-all active:scale-95 shadow-md"
                        >
                            {showAll ? 'Show Fewer Questions' : `View ${project.faqs.length - 4} More Questions`}
                        </button>
                    </div>
                )}

                {/* Bottom Footer Info - Simplified */}
                <div className="bg-slate-50/30 py-4 px-6 md:px-8 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-[11px] font-medium text-black">
                        Updated {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                    <a href="#inquiry-sidebar" className="text-[11px] font-bold text-black hover:underline flex items-center gap-1.5">
                        <i className="fa-solid fa-headset"></i>
                        Speak with an expert
                    </a>
                </div>

            </div>
        </section>
    );
};

export default FAQSection;
